/**
 * The in-place autonomy changes that are not already in another feature's api: take back an
 * "Allow for session", claim or release a discovered session, and open a project's autonomy
 * dialog from anywhere. Demo (?mock=1) changes the demo data in place.
 *
 *   POST   /api/approvals/session-allows/revoke   { sessionId, machineId, tool, pattern }  operator+
 *   POST   /api/sessions/:id/claim                { confirm: true }                       operator+
 *   DELETE /api/sessions/:id/claim                the claimer, or admin+
 */
import { create } from 'zustand';
import { SESSION_ALLOW_REVOKE_PATH } from '@orgus/protocol';
import type { ApprovalsState, Id, PolicyRule, Session, SessionAllow, SessionAllowRevoke, SessionClaim, SessionControlState } from '@orgus/protocol';
import { apiDelete, apiPost } from '../../lib/api.ts';
import { saveRule } from '../approvals/live.ts';
import { useOrgus } from '../../lib/store.ts';
import { demoClaim, demoRelease } from '../session-detail/demo.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const enc = encodeURIComponent;
const same = (a: SessionAllow, b: SessionAllowRevoke) => a.sessionId === b.sessionId && a.machineId === b.machineId && a.tool === b.tool && a.pattern === b.pattern;

export async function revokeSessionAllow(a: SessionAllow): Promise<void> {
  const body: SessionAllowRevoke = { sessionId: a.sessionId, machineId: a.machineId, tool: a.tool, pattern: a.pattern };
  if (!isMock()) await apiPost(SESSION_ALLOW_REVOKE_PATH, body);
  else await wait(250);
  // Show it at once; the hub's broadcast confirms it.
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState | undefined;
  if (cur) st.applyExt({ ext: 'approvals', type: 'state', data: { ...cur, sessionAllows: (cur.sessionAllows ?? []).filter((x) => !same(x, body)) } satisfies ApprovalsState });
}

function putClaim(sessionId: Id, claim: SessionClaim | null): void {
  const st = useOrgus.getState();
  const cur = st.ext['session-control'] as SessionControlState | undefined;
  if (!cur) return;
  const claims = cur.claims.filter((c) => c.sessionId !== sessionId);
  st.applyExt({ ext: 'session-control', type: 'state', data: { ...cur, claims: claim ? [...claims, claim] : claims } satisfies SessionControlState });
}

/** The session-control claim: an audited record that lifts a discovered session's read-only watch. */
export async function claimSession(s: Session): Promise<SessionClaim> {
  if (isMock()) {
    await wait(250);
    return demoClaim(s);
  }
  const r = await apiPost<{ claim: SessionClaim }>(`/api/sessions/${enc(s.id)}/claim`, { confirm: true });
  if (r?.claim) putClaim(s.id, r.claim);
  return r.claim;
}

export async function releaseClaim(s: Session): Promise<void> {
  if (isMock()) {
    await wait(250);
    demoRelease(s);
    return;
  }
  await apiDelete(`/api/sessions/${enc(s.id)}/claim`);
  putClaim(s.id, null);
}

// ---------------------------------------------------------------------------
// The project autonomy dialog, opened from a project's menu or a session drawer
// ---------------------------------------------------------------------------

interface AutonomyDialogState {
  projectId: Id | null;
  /** A rule being added or edited: the dialog steps aside for the rule editor (never two dialogs at once). */
  rule: { editing: PolicyRule | null } | null;
}

export const useProjectAutonomy = create<AutonomyDialogState>()(() => ({ projectId: null, rule: null }));

export function openProjectAutonomy(projectId: Id): void {
  useProjectAutonomy.setState({ projectId, rule: null });
}

export function closeProjectAutonomy(): void {
  useProjectAutonomy.setState({ projectId: null, rule: null });
}

export function editProjectRule(editing: PolicyRule | null): void {
  useProjectAutonomy.setState({ rule: { editing } });
}

export function backToProjectAutonomy(): void {
  useProjectAutonomy.setState({ rule: null });
}

/** Turn a rule on or off: the rule as it is, with `enabled` flipped. */
export async function setRuleEnabled(r: PolicyRule, enabled: boolean): Promise<void> {
  await saveRule({ scope: r.scope, match: r.match, decision: r.decision, reason: r.reason, enabled }, r.id);
}
